import { all, call, put, takeLatest, select, SagaReturnType } from 'redux-saga/effects';
import {
    cancelBookingError,
    cancelBookingRequest,
    cancelBookingSuccess,
    createBookingError,
    createBookingRequest,
    createBookingSuccess,
    getBookingDevicesError,
    getBookingDevicesLocationsError,
    getBookingDevicesLocationsRequest,
    getBookingDevicesLocationsSuccess,
    getBookingDevicesRequest,
    getBookingDevicesSuccess,
    getBookingsError,
    getBookingsRequest,
    getBookingsSuccess,
    getClosestBookingError,
    getClosestBookingRequest,
    getClosestBookingSuccess,
    getFullDeviceInfoError,
    getFullDeviceInfoRequest,
    getFullDeviceInfoSuccess,
    selectBooking,
    updateBookingError,
    updateBookingRequest,
    updateBookingSuccess,
} from './index';
import {
    cancelBooking,
    createBooking,
    getBookingDeviceFullInfo,
    getBookingDevices,
    getBookingDevicesLocations,
    getBookings,
    getClosestBooking,
    updateBooking,
} from '@pages/booking/api';
import { AxiosError } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import {
    BookingsRequestParams,
    CancelBookingRequestParams,
    ClosestBookingRequestParams,
    CreateBookingRequestParams,
    Device,
    DevicesSearchRequestParams,
    UpdateBookingRequestParams,
} from '@pages/booking/types';
import { SagaIterator } from 'redux-saga';
import { transformBookingDevicesResponse } from '@pages/booking/utils';

type GetBookingDevicesResponse = SagaReturnType<typeof getBookingDevices>;
export function* getBookingDevicesSaga(action: PayloadAction<DevicesSearchRequestParams>): SagaIterator {
    try {
        const response: GetBookingDevicesResponse = yield call(getBookingDevices, action.payload);
        yield put(getBookingDevicesSuccess({ ...response, data: transformBookingDevicesResponse(response) }));
    } catch (error) {
        yield put(getBookingDevicesError(error as AxiosError));
    }
}

type GetBookingDevicesLocationsResponse = SagaReturnType<typeof getBookingDevicesLocations>;
export function* getBookingDevicesLocationsSaga(): SagaIterator {
    try {
        const response: GetBookingDevicesLocationsResponse = yield call(getBookingDevicesLocations);
        yield put(getBookingDevicesLocationsSuccess(response));
    } catch (error) {
        yield put(getBookingDevicesLocationsError(error as AxiosError));
    }
}

type GetFullDeviceInfoResponse = SagaReturnType<typeof getBookingDeviceFullInfo>;
export function* getFullDeviceInfoSaga(action: PayloadAction<Device['pk']>): SagaIterator {
    try {
        const response: GetFullDeviceInfoResponse = yield call(getBookingDeviceFullInfo, action.payload);
        yield put(getFullDeviceInfoSuccess(response));
    } catch (error) {
        yield put(getFullDeviceInfoError(error as AxiosError));
    }
}

type GetBookingsResponse = SagaReturnType<typeof getBookings>;
export function* getBookingsSaga(action: PayloadAction<BookingsRequestParams>): SagaIterator {
    try {
        const response: GetBookingsResponse = yield call(getBookings, action.payload);
        yield put(getBookingsSuccess(response));
    } catch (error) {
        yield put(getBookingsError(error as AxiosError));
    }
}

type GetClosestBookingsResponse = SagaReturnType<typeof getClosestBooking>;
export function* getClosestBookingSaga(action: PayloadAction<ClosestBookingRequestParams>): SagaIterator {
    try {
        const response: GetClosestBookingsResponse = yield call(getClosestBooking, action.payload);
        yield put(getClosestBookingSuccess(response));
    } catch (error) {
        yield put(getClosestBookingError(error as AxiosError));
    }
}

type BookingCreateResponse = SagaReturnType<typeof createBooking>;
export function* createBookingSaga(action: PayloadAction<CreateBookingRequestParams>): SagaIterator {
    const lastGetBookingsParams = yield select((state) => state.booking.lastGetBookingsParams);
    const { selectMode, ...bookingData } = action.payload;

    try {
        const response: BookingCreateResponse = yield call(createBooking, bookingData);
        yield put(createBookingSuccess(response));

        if (lastGetBookingsParams) {
            yield put(getBookingsRequest({ ...lastGetBookingsParams, requestMode: 'background-fetch' }));
        }
        if (selectMode) {
            yield put(
                selectBooking({
                    deviceId: response.data.device,
                    booking: response.data,
                }),
            );
        }
    } catch (error) {
        yield put(createBookingError(error as AxiosError));
    }
}

type BookingUpdateResponse = SagaReturnType<typeof updateBooking>;
export function* updateBookingSaga(action: PayloadAction<UpdateBookingRequestParams>): SagaIterator {
    const lastGetBookingsParams = yield select((state) => state.booking.lastGetBookingsParams);

    try {
        const response: BookingUpdateResponse = yield call(updateBooking, action.payload);
        yield put(updateBookingSuccess(response));

        if (lastGetBookingsParams) {
            yield put(getBookingsRequest({ ...lastGetBookingsParams, requestMode: 'background-fetch' }));
        }
    } catch (error) {
        yield put(updateBookingError(error as AxiosError));
    }
}

type BookingCancelResponse = SagaReturnType<typeof cancelBooking>;
export function* cancelBookingSaga(action: PayloadAction<CancelBookingRequestParams>): SagaIterator {
    try {
        const { data }: BookingCancelResponse = yield call(cancelBooking, action.payload);
        yield put(cancelBookingSuccess({ ...action.payload, newBookingObject: data }));
    } catch (error) {
        yield put(cancelBookingError(error as AxiosError));
    }
}

export default function* bookingSagaWatcher() {
    yield all([
        takeLatest(getBookingDevicesRequest.type, getBookingDevicesSaga),
        takeLatest(getBookingDevicesLocationsRequest.type, getBookingDevicesLocationsSaga),
        takeLatest(getFullDeviceInfoRequest.type, getFullDeviceInfoSaga),
        takeLatest(getBookingsRequest.type, getBookingsSaga),
        takeLatest(getClosestBookingRequest.type, getClosestBookingSaga),
        takeLatest(createBookingRequest.type, createBookingSaga),
        takeLatest(updateBookingRequest.type, updateBookingSaga),
        takeLatest(cancelBookingRequest.type, cancelBookingSaga),
    ]);
}
